import { useCallback, useEffect, useState } from 'react';
import EnhancedListPickerModal from '../../Common/components/EnhancedListPickerModal';
import useUserOrg from '../../Authentication/components/hooks/useUserOrg';
import useEnhancedDeviceAssignments from './hooks/useEnhancedDeviceAssignments';
import useDeviceKeys from './hooks/useDeviceKeys';
import useFeatureFlags from '../../Features/components/hooks/useFeatureFlags';
import { FeatureFlags } from '../../Features/lib/constants';
import useOrganizations from '../../Organizations/hooks/useOrganizations';

interface EnhancedGalleryDeviceAssignmentsProps {
    galleryId: number;
}

export default function EnhancedGalleryDeviceAssignments({
    galleryId,
}: EnhancedGalleryDeviceAssignmentsProps) {
    const { orgId } = useUserOrg();
    const { featureFlags } = useFeatureFlags();
    const { listOrganizations } = useOrganizations();

    const [devices, setDevices] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [showPicker, setShowPicker] = useState(false);
    const [organizations, setOrganizations] = useState<
        { id: number; name: string }[]
    >([]);
    const [selectedOrgId, setSelectedOrgId] = useState(orgId);
    const [crossOrgSelections, setCrossOrgSelections] = useState<
        Record<number, number[]>
    >({});

    const isGlobalAdmin = featureFlags[FeatureFlags.GLOBAL_ADMIN_V1];
    const { getDeviceKeys } = useDeviceKeys(selectedOrgId);
    const { listDeviceAssignmentsForGallery, addDeviceAssignmentsForGallery } =
        useEnhancedDeviceAssignments();

    // Load organizations for global admins
    useEffect(() => {
        if (isGlobalAdmin) {
            const loadOrganizations = async () => {
                try {
                    const orgList = await listOrganizations();
                    setOrganizations(orgList);
                } catch (error) {
                    console.error('Error loading organizations:', error);
                }
            };
            loadOrganizations();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isGlobalAdmin]);

    useEffect(() => {
        let isMounted = true;
        const load = async () => {
            try {
                const assignments = await listDeviceAssignmentsForGallery(
                    galleryId,
                );

                const devices = await getDeviceKeys();

                isMounted && setAssignments(assignments);
                isMounted && setDevices(devices);
            } catch (error) {
                console.error(error);
            }
        };

        load();
        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOrgId]);

    const saveAssignments = useCallback(
        async (
            newAssignments: number[],
            crossOrgSelections?: Record<number, number[]>,
        ) => {
            try {
                // If we have cross-org selections, include them in the API call
                if (
                    crossOrgSelections &&
                    Object.keys(crossOrgSelections).length > 0
                ) {
                    // Convert to the format expected by the enhanced API
                    const crossOrgDeviceIds = Object.entries(
                        crossOrgSelections,
                    ).map(([orgIdStr, deviceIds]) => ({
                        orgId: parseInt(orgIdStr),
                        deviceIds,
                    }));
                    await addDeviceAssignmentsForGallery(
                        galleryId,
                        newAssignments,
                        crossOrgDeviceIds,
                    );
                } else {
                    await addDeviceAssignmentsForGallery(
                        galleryId,
                        newAssignments,
                    );
                }

                const response = await listDeviceAssignmentsForGallery(
                    galleryId,
                );

                setAssignments(response);
                // Clear cross-org selections after successful save
                setCrossOrgSelections({});
            } catch (error) {
                console.error(error);
            }
            setShowPicker(false);
        },
        [
            addDeviceAssignmentsForGallery,
            galleryId,
            listDeviceAssignmentsForGallery,
        ],
    );

    const handleOrgChange = useCallback((newOrgId: number) => {
        setSelectedOrgId(newOrgId);
        setDevices([]); // Clear devices while loading
    }, []);

    return (
        <>
            <span className="center" onClick={() => setShowPicker(true)}>
                {assignments.length === 0
                    ? 'none'
                    : assignments.map(({ deviceName: name }) => `${name} `)}
                {' |'}
                <i className="bi bi-card-checklist"></i>
            </span>
            {showPicker && (
                <EnhancedListPickerModal
                    title="Select Devices"
                    list={devices}
                    selectedIds={assignments.map(({ deviceId: id }) => id)}
                    onClose={() => setShowPicker(false)}
                    onSave={saveAssignments}
                    showOrgSelector={isGlobalAdmin}
                    organizations={organizations}
                    selectedOrgId={selectedOrgId}
                    onOrgChange={handleOrgChange}
                    crossOrgSelections={crossOrgSelections}
                    key={selectedOrgId}
                />
            )}
        </>
    );
}
